import { atlasDay, atlasDayIsStale, type AtlasLocation } from "./atlas";
import { inAtlasRegion, type AtlasRegion } from "./atlas-regions";
import { isSkiInSeason, isSouthernSkiInSeason } from "./seasons";

/** Regional display window: the existing ski season plus one calendar month each side. */
export function atlasSnowInSeason(date: string, latitude: number): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(latitude) || Math.abs(latitude) > 90) return false;
  const parsed = new Date(`${date}T12:00:00Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) return false;
  const inSeason = latitude < 0 ? isSouthernSkiInSeason : isSkiInSeason;
  return [-1, 0, 1].some(offset => inSeason(new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + offset, 1))));
}

export type AtlasSnowWindow = "1" | "3" | "7" | "next";
export const atlasShiftDate = (date: string, days: number) => new Date(Date.parse(`${date}T12:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
export function atlasTimelineDates(today: string, past: boolean): string[] {
  return Array.from({ length: 7 }, (_, i) => atlasShiftDate(today, past ? i - 7 : i));
}
export function atlasSnowDates(anchor: string, window: AtlasSnowWindow): string[] {
  const count = window === "next" ? 3 : Number(window);
  return Array.from({ length: count }, (_, i) => atlasShiftDate(anchor, window === "next" ? i : i - count));
}
export function atlasMedian(values: readonly number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a,b) => a-b), middle = Math.floor(sorted.length/2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle-1]+sorted[middle])/2;
}
export type AtlasSnowSummary = {
  median: number | null; low: number | null; high: number | null;
  reporting: number; total: number; cells: number; dates: string[];
  leaders: { location: AtlasLocation; snowCm: number }[];
  daily: { date: string; median: number | null; reporting: number }[];
};
/** Sum through time at each resort, then describe the reporting resorts. Never sum resorts. */
export function summariseAtlasSnow(locations: readonly AtlasLocation[], region: AtlasRegion | undefined, anchor: string, window: AtlasSnowWindow, now = Date.now()): AtlasSnowSummary {
  const members = locations.filter(location => location.type === "ski" && (!region || inAtlasRegion(location, region)));
  const dates = atlasSnowDates(anchor, window);
  const snowOn = (location: AtlasLocation, date: string): number | null => {
    const day = atlasDay(location, date);
    // History cannot borrow an old forecast, including at timezone boundaries.
    if (!day || (window !== "next" && day.source !== "modelled-past") || (window === "next" && day.source === "modelled-past") || atlasDayIsStale(location, date, now)) return null;
    return day.w[1] !== null && Number.isFinite(day.w[1]) && day.w[1] >= 0 ? day.w[1] : null;
  };
  const reported = members.flatMap(location => {
    const values = dates.map(date => snowOn(location, date));
    return values.some(value => value === null) ? [] : [{ location, snowCm: values.reduce<number>((sum, value) => sum + value!, 0) }];
  });
  const values = reported.map(item => item.snowCm);
  const cells = new Set(reported.map(({ location }) => `${Math.floor(location.lng*2)}:${Math.floor(location.lat*2)}`)).size;
  return { median: atlasMedian(values), low: values.length ? Math.min(...values) : null, high: values.length ? Math.max(...values) : null,
    reporting: reported.length, total: members.length, cells, dates,
    leaders: reported.sort((a,b) => b.snowCm-a.snowCm || a.location.name.localeCompare(b.location.name)).slice(0,3),
    daily: dates.map(date => { const values = members.flatMap(location => { const value = snowOn(location,date); return value === null ? [] : [value]; }); return { date, median: atlasMedian(values), reporting: values.length }; }),
  };
}
